import { ContractStatusOption } from '@/graphql/generated';

export type ContractCategoryKey =
  | 'endNext30Days'
  | 'newContracts'
  | 'gettingStartedNext30Days'
  | 'initiated'
  | 'rentUpdates';

export type ContractCategoryCount = {
  key: ContractCategoryKey;
  count: number;
};

export type ContractLike = {
  statusOption?: ContractStatusOption;
  startDate?: string | number | Date | null;
  finishDate?: string | number | Date | null;
  createdAt?: string | number | Date | null;
};

const ACTIVE_STATUSES = [
  ContractStatusOption.Initiated,
  ContractStatusOption.Booking,
];
const NEW_STATUS = ContractStatusOption.Booking;
const NOT_ISSUED_STATUS = ContractStatusOption.NotIssued;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDate = (
  value: string | number | Date | null | undefined
): Date | null => {
  if (value == null) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isWithinNext30Days = (date: Date): boolean => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(today.getTime() + 30 * MS_PER_DAY);
  return date >= today && date <= end;
};

export const getContractCategoryCounts = (
  contracts: ContractLike[] | null | undefined
): ContractCategoryCount[] => {
  if (!contracts?.length) {
    return [
      { key: 'endNext30Days', count: 0 },
      { key: 'newContracts', count: 0 },
      { key: 'gettingStartedNext30Days', count: 0 },
      { key: 'initiated', count: 0 },
      { key: 'rentUpdates', count: 0 },
    ];
  }

  let endNext30 = 0;
  let newContracts = 0;
  let gettingStartedNext30 = 0;
  let initiated = 0;
  let rentUpdates = 0;

  contracts.forEach((contract) => {
    const status = contract.statusOption;
    const startDate = parseDate(contract.startDate);
    const finishDate = parseDate(contract.finishDate);

    if (
      finishDate &&
      isWithinNext30Days(finishDate) &&
      status &&
      ACTIVE_STATUSES.includes(status)
    ) {
      endNext30 += 1;
    }
    if (status === NEW_STATUS) {
      newContracts += 1;
    }
    if (startDate && isWithinNext30Days(startDate)) {
      gettingStartedNext30 += 1;
    }
    if (status === ContractStatusOption.Initiated) {
      initiated += 1;
    }
    if (status === NOT_ISSUED_STATUS) {
      rentUpdates += 1;
    }
  });

  return [
    { key: 'endNext30Days', count: endNext30 },
    { key: 'newContracts', count: newContracts },
    { key: 'gettingStartedNext30Days', count: gettingStartedNext30 },
    { key: 'initiated', count: initiated },
    { key: 'rentUpdates', count: rentUpdates },
  ];
};
